import { api } from "../../lib";

export interface ReportTemplate {
  id: string;
  name: string;
  description: string | null;
  isDefault: boolean;
  isActive: boolean;
}

export interface LinkedPractice {
  practice: {
    id: string;
    name: string;
  };
  isPrimary: boolean;
}

export interface LinkedSurgeryCenter {
  surgeryCenterLab: {
    id: string;
    name: string;
    type: "surgery_center" | "lab";
  };
  isPrimary: boolean;
}

export interface Physician {
  id: string;
  firstName: string;
  lastName: string;
  email?: string | null;
  npi: string | null;
  phone: string | null;
  fax: string | null;
  preferredPathologistId: string | null;
  reportTemplateId: string | null;
  isActive: boolean;
  offerTissueCypher: boolean;
  createdAt: string;
  updatedAt: string;
  preferredPathologist?: { fullName: string } | null;
  reportTemplate?: ReportTemplate | null;
  // snake_case aliases for backward compatibility
  first_name?: string;
  last_name?: string;
  preferred_pathologist_id?: string | null;
  report_template_id?: string | null;
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;
  preferred_pathologist?: { full_name: string } | null;
  report_template?: ReportTemplate | null;
  isRemote?: boolean;
  is_remote?: boolean;
  remoteShippingStreet?: string | null;
  remote_shipping_street?: string | null;
  remoteShippingCity?: string | null;
  remote_shipping_city?: string | null;
  remoteShippingState?: string | null;
  remote_shipping_state?: string | null;
  remoteShippingZip?: string | null;
  remote_shipping_zip?: string | null;
  address?: PhysicianRemoteShippingAddressFields | null;
  remoteAddress?: PhysicianRemoteShippingAddressFields | null;
}

export interface CreatePhysicianInput {
  firstName: string;
  lastName: string;
  email?: string | null;
  npi?: string;
  phone?: string;
  fax?: string;
  preferredPathologistId?: string;
  reportTemplateId?: string;
  isActive?: boolean;
  offerTissueCypher?: boolean;
}

export interface UpdatePhysicianInput extends Partial<CreatePhysicianInput> {
  id: string;
}

export interface PhysicianRemoteShippingAddressFields {
  street?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
}

export interface PhysicianRemoteShippingAddressPayload {
  isRemote: boolean;
  address?: PhysicianRemoteShippingAddressFields;
}

export interface PhysicianRemoteShippingAddress
  extends PhysicianRemoteShippingAddressPayload {
  physicianId: string;
}

// Normalize physician to have both camelCase and snake_case properties
function normalizePhysician(physician: Physician): Physician {
  return {
    ...physician,
    // Add snake_case aliases
    first_name: physician.firstName,
    last_name: physician.lastName,
    preferred_pathologist_id: physician.preferredPathologistId,
    report_template_id: physician.reportTemplateId,
    is_active: physician.isActive,
    created_at: physician.createdAt,
    updated_at: physician.updatedAt,
    preferred_pathologist: physician.preferredPathologist
      ? { full_name: physician.preferredPathologist.fullName }
      : null,
    report_template: physician.reportTemplate,
    isRemote: physician.isRemote ?? physician.is_remote ?? false,
    is_remote: physician.is_remote ?? physician.isRemote ?? false,
    remoteShippingStreet:
      physician.remoteShippingStreet ||
      physician.remote_shipping_street ||
      physician.address?.street ||
      null,
    remote_shipping_street:
      physician.remote_shipping_street ||
      physician.remoteShippingStreet ||
      physician.address?.street ||
      null,
    remoteShippingCity:
      physician.remoteShippingCity ||
      physician.remote_shipping_city ||
      physician.address?.city ||
      null,
    remote_shipping_city:
      physician.remote_shipping_city ||
      physician.remoteShippingCity ||
      physician.address?.city ||
      null,
    remoteShippingState:
      physician.remoteShippingState ||
      physician.remote_shipping_state ||
      physician.address?.state ||
      null,
    remote_shipping_state:
      physician.remote_shipping_state ||
      physician.remoteShippingState ||
      physician.address?.state ||
      null,
    remoteShippingZip:
      physician.remoteShippingZip ||
      physician.remote_shipping_zip ||
      physician.address?.zip ||
      null,
    remote_shipping_zip:
      physician.remote_shipping_zip ||
      physician.remoteShippingZip ||
      physician.address?.zip ||
      null,
    address: physician.address || physician.remoteAddress || null,
    remoteAddress: physician.remoteAddress || physician.address || null,
  };
}

// Helper to sanitize input - convert empty strings to null for UUID fields
function sanitizePhysicianInput(
  input: CreatePhysicianInput | Omit<UpdatePhysicianInput, "id">
): Record<string, unknown> {
  return {
    ...input,
    // Convert empty strings to null for UUID fields
    preferredPathologistId: input.preferredPathologistId || null,
    reportTemplateId: input.reportTemplateId || null,
    // Convert empty strings to null for optional string fields
    npi: input.npi || null,
    phone: input.phone || null,
    fax: input.fax || null,
    email:
      typeof input.email === "string" && input.email.trim() === ""
        ? null
        : input.email,
  };
}

export const physiciansService = {
  getAll: async () => {
    const physicians = await api.get<Physician[]>("/admin/physicians");
    return physicians.map(normalizePhysician);
  },

  getById: async (id: string) => {
    const physician = await api.get<Physician>(`/admin/physicians/${id}`);
    return normalizePhysician(physician);
  },

  create: async (input: CreatePhysicianInput) => {
    const sanitizedInput = sanitizePhysicianInput(input);
    const physician = await api.post<Physician>(
      "/admin/physicians",
      sanitizedInput
    );
    return normalizePhysician(physician);
  },

  update: async (input: UpdatePhysicianInput) => {
    const { id, ...updates } = input;
    const sanitizedUpdates = sanitizePhysicianInput(updates);
    const physician = await api.put<Physician>(
      `/admin/physicians/${id}`,
      sanitizedUpdates
    );
    return normalizePhysician(physician);
  },

  delete: async (id: string) => {
    await api.delete<void>(`/admin/physicians/${id}`);
  },

  toggleActive: async (id: string, isActive: boolean) => {
    const physician = await api.put<Physician>(`/admin/physicians/${id}`, {
      isActive,
    });
    return normalizePhysician(physician);
  },

  getLinkedPractices: async (id: string) => {
    return api.get<LinkedPractice[]>(
      `/admin/physicians/${id}/linked-practices`
    );
  },

  getLinkedSurgeryCenters: async (id: string) => {
    return api.get<LinkedSurgeryCenter[]>(
      `/admin/physicians/${id}/linked-surgery-centers`
    );
  },

  updateRemoteShippingAddress: async (
    physicianId: string,
    payload: PhysicianRemoteShippingAddressPayload
  ): Promise<PhysicianRemoteShippingAddress> => {
    return api.put<PhysicianRemoteShippingAddress>(
      `/admin/physicians/${physicianId}/remote-shipping-address`,
      payload
    );
  },
};
